"use client";

import { useAudioPlayback } from "../../hooks/use-audio-playback";
import { AudioPlayer } from "../AudioPlayer";

type MediaAudioPlayerProps = {
  allowDownload?: boolean;
  className?: string;
  durationSeconds?: number;
  src: string;
  title: string;
};

/**
 * Episode-agnostic audio player. Composes the same `useAudioPlayback` hook and
 * `AudioPlayer` presentation as `AudioPlayerHero`, without that component's
 * Caregiver Chats cover art, back link, and Spotify chrome.
 */
function MediaAudioPlayer({
  allowDownload,
  className,
  durationSeconds = 0,
  src,
  title,
}: MediaAudioPlayerProps) {
  const {
    audioRef,
    currentTimeSeconds,
    durationSeconds: playbackDurationSeconds,
    hasError,
    isPlaying,
    isReady,
    seekToSeconds,
    skipBySeconds,
    togglePlayPause,
  } = useAudioPlayback({
    audioSource: src,
    initialDurationSeconds: durationSeconds,
  });

  return (
    <div className={className}>
      <audio preload="metadata" ref={audioRef} src={src} />
      <AudioPlayer
        ariaLabel={`Audio player: ${title}`}
        currentTimeSeconds={currentTimeSeconds}
        durationSeconds={playbackDurationSeconds}
        error={hasError ? "Audio playback is unavailable." : undefined}
        isLoading={!isReady && !hasError}
        isPlaying={isPlaying}
        onPlayPause={togglePlayPause}
        onSeek={seekToSeconds}
        onSkip={skipBySeconds}
      />
      {allowDownload ? (
        <a
          className="mt-2 inline-block font-jakarta text-sm underline hover:no-underline"
          download
          href={src}
        >
          Download audio
        </a>
      ) : null}
    </div>
  );
}

export { MediaAudioPlayer };
